import type {
  AlertHistoryDetails,
  AlertHistoryGeofence,
  AlertHistoryItem,
  AlertHistoryLocation,
} from "../domain";
import {
  cameraAlarmDetailsDtoSchema,
  downtimeDetailsDtoSchema,
  sensorDetailsDtoSchema,
  geofenceDetailsDtoSchema,
  speedDetailsDtoSchema,
  type AlertHistoryDetailsDto,
  type AlertHistoryItemDto,
} from "./alertHistoryDtos";

/** A location is shown only when both coordinates are real ones (Scenario
 *  28: never an invalid location). */
function locationFromDto(
  location: AlertHistoryItemDto["location"],
): AlertHistoryLocation | null {
  const latitude = location?.latitude;
  const longitude = location?.longitude;
  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180
  ) {
    return null;
  }
  return { latitude, longitude };
}

export function alertHistoryItemFromDto(
  dto: AlertHistoryItemDto,
): AlertHistoryItem {
  return {
    id: dto.id,
    occurredAt: dto.occurredAt,
    company: dto.company ?? null,
    asset:
      dto.asset === null || dto.asset === undefined
        ? null
        : { ...dto.asset, code: dto.asset.code ?? "" },
    alertType: dto.alertType,
    alertNameEn: dto.alertNameEn,
    alertNameAr: dto.alertNameAr,
    triggeredValueEn: dto.triggeredValueEn ?? "",
    triggeredValueAr: dto.triggeredValueAr ?? "",
    location: locationFromDto(dto.location),
    locationLabel: dto.locationLabel?.trim() || null,
  };
}

function historyGeofenceFromDto(
  dto: { readonly id: string; readonly name?: string | null | undefined } | null | undefined,
): AlertHistoryGeofence | null {
  return dto === null || dto === undefined ? null : { id: dto.id, name: dto.name ?? "" };
}

export function alertHistoryDetailsFromDto(
  dto: AlertHistoryDetailsDto | null | undefined,
): AlertHistoryDetails {
  if (dto === null || dto === undefined) return { type: "OTHER" };
  switch (dto.type) {
    case "SPEED": {
      const speed = speedDetailsDtoSchema.parse(dto);
      return {
        type: "SPEED",
        recordedSpeedKph: speed.recordedSpeedKph ?? null,
        speedLimitKph: speed.speedLimitKph ?? null,
        geofence: historyGeofenceFromDto(speed.geofence),
      };
    }
    case "ENTERED_GEOFENCE":
    case "EXITED_GEOFENCE":
      return {
        type: dto.type,
        geofence: historyGeofenceFromDto(geofenceDetailsDtoSchema.parse(dto).geofence),
      };
    case "DOWNTIME":
      return {
        type: "DOWNTIME",
        durationMinutes:
          downtimeDetailsDtoSchema.parse(dto).durationMinutes ?? null,
      };
    case "SENSOR": {
      const sensorDetails = sensorDetailsDtoSchema.parse(dto);
      const { sensor, condition } = sensorDetails;
      return {
        type: "SENSOR",
        sensor:
          sensor === null || sensor === undefined
            ? null
            : typeof sensor === "string"
              ? { id: null, nameEn: sensor, nameAr: sensor, sensorType: null }
              : {
                  id: sensor.id ?? null,
                  nameEn: sensor.nameEn ?? sensor.name ?? null,
                  nameAr: sensor.nameAr ?? sensor.name ?? null,
                  sensorType: sensor.sensorType ?? null,
                },
        quantity: sensorDetails.quantity ?? null,
        triggeredValue: sensorDetails.triggeredValue ?? null,
        unit: sensorDetails.unit ?? null,
        condition:
          condition === null || condition === undefined
            ? null
            : {
                operator: condition.operator,
                value: condition.value ?? null,
                valueTo: condition.valueTo ?? null,
              },
      };
    }
    case "DRIVER_BEHAVIOUR":
    case "ROAD_SAFETY": {
      const alarm = cameraAlarmDetailsDtoSchema.parse(dto);
      return {
        type: dto.type,
        alarmType: alarm.alarmType ?? null,
        behaviourEn: alarm.behaviourEn ?? null,
        behaviourAr: alarm.behaviourAr ?? null,
        level: alarm.level ?? null,
        driver:
          alarm.driver === null || alarm.driver === undefined
            ? null
            : {
                id: alarm.driver.id,
                nameEn: alarm.driver.nameEn ?? "",
                nameAr: alarm.driver.nameAr ?? "",
              },
      };
    }
    default:
      return { type: "OTHER" };
  }
}
